// One experiment, of whatever kind, on one page: what it is, where its
// allocation sits and the control that moves it, then the evidence behind it in
// tabs. The register (`/experiments`) answers "what is running"; this page
// answers "what is THIS one doing, and should it keep running".
//
// The allocation control is the one write on the page, and it is the same
// control for every kind. A product experiment names its non-baseline arms, a
// prompt slot its non-default approaches, an injection its three dials; all of
// them post ONE payload with ONE reason to
// `POST /api/admin/experimentation/{key}/allocation`, which validates it per
// kind. The two-step commit (slide, read the sentence, give a reason, apply) is
// the grammar every dial in this console shares, because the control moves live
// traffic and a slider that commits on release lets a slipped mouse re-dial an
// experiment.
//
// Every tab reads what already exists. Outcomes is the A/B readout the old
// `/ab-tests` page rendered (still behind its own `ab_tests` grant, so a reader
// who holds only `system_admin` is told so in a sentence rather than shown a
// gate); Strip, Cells and Guardrails are cuts of the worker's experimentation
// snapshot; Timeline is the lifecycle ledger filtered to this key; Triggers is
// the injection's declared `when` beside the vocabulary it is drawn from. An
// UNMEASURED value prints "-", never 0 (`lib/experimentation.ts`).
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { Badge, TableExport } from "@recruiter/shared";
import type {
  AdminExperimentRecord,
  AdminExperimentationResponse,
  AdminLifecycleEvent,
} from "@recruiter/shared";

import { AreaBoundary, AreaGate } from "../components/area-gate";
import { DecisionPanel, GuardrailThresholds, TrustedTags } from "../components/experiment-controls";
import { AllocationEditor } from "../components/experiment-allocation";
import { EmptyState, LoadingState, errorMessage } from "../components/states";
import {
  EXPERIMENT_TAB_LABELS,
  type ExperimentTab,
  INJECTION_WINDOWS,
  armShares,
  formatInjectionCells,
  formatRate,
  formatStrip,
  formatTransition,
  summariseAllocation,
  tabsFor,
} from "../lib/experimentation";
import { formatDateTime } from "../lib/format";
import {
  experimentationLifecycleQuery,
  experimentationQuery,
  experimentationTriggersQuery,
  experimentIssuesQuery,
  promptSectionsQuery,
} from "../lib/queries";
import { useHoldsAnyArea } from "../lib/use-access";
import { ExperimentOutcomes } from "./ab-tests";
import "./experiments.css";
import "./experiment-detail.css";

const LIFECYCLE_LIMIT = 50;

/** What the Issues tab shows before the first response names the real window.
 *  The server measures it on the A/B tick and reports it back (#1260); this is a
 *  placeholder for the pending render, not a second source of truth. */
const ISSUES_WINDOW_HOURS = 24;

type Props = {
  experimentKey: string;
  tab: ExperimentTab | undefined;
  onTabChange: (next: ExperimentTab | undefined) => void;
  /** May this operator move the allocation? `system_admin` at `full`. */
  canAct: boolean;
  accessKnown: boolean;
};

export function ExperimentDetailPage({
  experimentKey,
  tab,
  onTabChange,
  canAct,
  accessKnown,
}: Props) {
  const snapshot = useQuery(experimentationQuery());

  if (snapshot.isPending) return <LoadingState label="Reading the experiment…" />;
  if (snapshot.isError) {
    return (
      <AreaBoundary
        area="system_admin"
        error={snapshot.error}
        onRetry={() => void snapshot.refetch()}
      />
    );
  }

  const data = snapshot.data;
  const record = data.experiments.find((e) => e.key === experimentKey);
  if (!record) {
    return (
      <div className="exd-page">
        <Link className="ad-link" to="/experiments">
          Back to experiments
        </Link>
        <EmptyState
          title="No such experiment"
          message={
            data.state !== "ready"
              ? "The snapshot is not computed yet; the worker builds it on its half-hourly tick, and this page will list the experiment once it has."
              : `Nothing in the registry is keyed ${experimentKey}. It may have been retired from the code.`
          }
        />
      </div>
    );
  }

  const tabs = tabsFor(record.kind);
  const active: ExperimentTab = tab && tabs.includes(tab) ? tab : tabs[0]!;

  return (
    <div className="exd-page">
      <Link className="ad-link" to="/experiments">
        Back to experiments
      </Link>
      <Header record={record} />
      <AllocationControl record={record} canAct={canAct} accessKnown={accessKnown} />
      <GuardrailThresholds record={record} canAct={canAct} />
      <DecisionPanel record={record} canAct={canAct} />

      <div className="exd-tabs" role="tablist" aria-label="Experiment detail sections">
        {tabs.map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={active === name}
            className={`exd-tab${active === name ? " exd-tab--active" : ""}`}
            onClick={() => onTabChange(name === tabs[0] ? undefined : name)}
          >
            {EXPERIMENT_TAB_LABELS[name]}
          </button>
        ))}
      </div>

      <div className="exd-panel" role="tabpanel">
        <TabPanel tab={active} record={record} data={data} canAct={canAct} />
      </div>
    </div>
  );
}

/** What it is: kind, unit, arms with the baseline named, label, where the
 *  allocation sits, and which tier and source declared it. */
function Header({ record }: { record: AdminExperimentRecord }) {
  const baseline = record.arms[0];
  return (
    <header className="exd-head">
      <div className="exd-head__title-row">
        <h1 className="ad-page__title">{record.label || record.key}</h1>
        <Badge tone="neutral">{record.kind}</Badge>
        <Badge tone="neutral">per {record.unit}</Badge>
        <Badge tone={record.tier === "released" ? "success" : "warning"}>{record.tier}</Badge>
      </div>
      <p className="exd-head__facts">
        <code className="exp-table__key">{record.key}</code>
        {" · "}declared in <code>{record.source}</code>
      </p>
      <p className="exd-head__facts">
        {/* Arms with the share each one holds, baseline LABELLED. The share
            comes off the wire (`record.armAllocation`), computed once by the
            backend, rather than being re-derived here: the console's own copy
            of that rule gave every baseline not called `default` a flat 0%. */}
        Arms:{" "}
        {armShares(record).map((share, i, all) => (
          <span key={share.arm} className="exd-head__arm">
            <code>{share.arm}</code> {share.pct}%{share.baseline ? " (baseline)" : ""}
            {i < all.length - 1 ? ", " : ""}
          </span>
        ))}
        {baseline === undefined ? <span>none declared</span> : null}
      </p>
      <p className="exd-head__facts">
        Allocation: <strong>{summariseAllocation(record)}</strong>
      </p>
    </header>
  );
}

/** The allocation card. The control itself is `components/experiment-allocation`,
 *  shared with the register row, so a dial behaves identically wherever it is
 *  reached from and there is exactly one place the per-kind shapes are written
 *  down. Read-only System Admins see the positions with the sliders disabled. */
function AllocationControl({
  record,
  canAct,
  accessKnown,
}: {
  record: AdminExperimentRecord;
  canAct: boolean;
  accessKnown: boolean;
}) {
  return (
    <section className="ad-section">
      <h2 className="ad-section__title">Allocation</h2>
      <AllocationEditor record={record} canAct={canAct} accessKnown={accessKnown} />
    </section>
  );
}

function TabPanel({
  tab,
  record,
  data,
  canAct,
}: {
  tab: ExperimentTab;
  record: AdminExperimentRecord;
  data: AdminExperimentationResponse;
  canAct: boolean;
}) {
  switch (tab) {
    case "outcomes":
      return <OutcomesTab record={record} />;
    case "strip":
      return <StripTab record={record} computing={data.state !== "ready"} />;
    case "cells":
      return <CellsTab record={record} data={data} />;
    case "guardrails":
      return <GuardrailsTab record={record} data={data} />;
    case "timeline":
      return <TimelineTab experimentKey={record.key} />;
    case "triggers":
      return <TriggersTab record={record} canAct={canAct} />;
    case "issues":
      return <IssuesTab record={record} />;
  }
}

/** The A/B readout, for a product experiment. It lives behind its own grant
 *  (`ab_tests`, aggregate-only, shared with Version Cohorts), which
 *  `system_admin` does not imply: a reader without it gets a sentence, not a
 *  gate panel in the middle of a page they are otherwise entitled to. */
function OutcomesTab({ record }: { record: AdminExperimentRecord }) {
  const holdsAbTests = useHoldsAnyArea(["ab_tests"]);
  if (record.kind !== "ui") {
    return (
      <p className="exp-section__sub">
        Funnel outcomes are measured for product experiments only, by user. A{" "}
        <code>{record.kind}</code> experiment is judged on its per-arm strip
        {record.kind === "injection" ? " and its cells" : ""}, in the tabs beside this one.
      </p>
    );
  }
  if (!holdsAbTests) {
    return (
      <p className="exp-section__sub">
        The funnel readout needs the <strong>Playground</strong> area (<code>ab_tests</code>), which
        your access does not include. Ask a System Admin for it to see both arms across the funnel
        steps here.
      </p>
    );
  }
  return (
    <AreaGate area="ab_tests">
      <ExperimentOutcomes experiment={record.key} />
    </AreaGate>
  );
}

/** The operational strip per arm: calls, cost per call, error rate, p95, from
 *  `agent_trace.stamp` over the snapshot's window. */
function StripTab({ record, computing }: { record: AdminExperimentRecord; computing: boolean }) {
  const strips = record.strips.map(formatStrip);
  return (
    <section className="exp-section">
      <p className="exp-section__sub">
        Whether each arm is safe to keep running: model calls, cost per call, error rate and p95
        latency from the traces stamped with this experiment.
        {computing ? " Not computed yet, the worker builds it on its half-hourly tick." : ""}
      </p>
      {strips.length === 0 ? (
        <p className="exp-section__empty">No traces carry this experiment yet.</p>
      ) : (
        <div className="exp-table-wrap">
          <table className="exp-table">
            <TableExport name="experiment-traces" label="traces" />
            <thead>
              <tr>
                <th scope="col">Arm</th>
                <th scope="col" className="exp-table__num">
                  Calls
                </th>
                <th scope="col" className="exp-table__num">
                  Cost/call
                </th>
                <th scope="col" className="exp-table__num">
                  Error rate
                </th>
                <th scope="col" className="exp-table__num">
                  p95
                </th>
              </tr>
            </thead>
            <tbody>
              {strips.map((strip) => (
                <tr key={strip.arm}>
                  <td>
                    <code>{strip.arm}</code>
                  </td>
                  <td className="exp-table__num">{strip.calls}</td>
                  <td className="exp-table__num">{strip.cost}</td>
                  <td className="exp-table__num">{strip.errors}</td>
                  <td className="exp-table__num">{strip.p95}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** The 2x2 plus control an injection's dials produced, per window. Counts from
 *  the worker's snapshot; "-" until the first tick lands. */
function CellsTab({
  record,
  data,
}: {
  record: AdminExperimentRecord;
  data: AdminExperimentationResponse;
}) {
  return (
    <section className="exp-section">
      <p className="exp-section__sub">
        Turns by cell. Control is the unenrolled rest; inside the exposure, every turn lands in one
        of the four cells by whether the trigger fired and whether the text was sent.
        {data.state !== "ready" ? " Not computed yet." : ""}
      </p>
      <div className="exp-table-wrap">
        <table className="exp-table">
          <TableExport name="experiment-windows" label="the window breakdown" />
          <thead>
            <tr>
              <th scope="col">Window</th>
              <th scope="col" className="exp-table__num">
                Turns
              </th>
              <th scope="col" className="exp-table__num">
                Control
              </th>
              <th scope="col" className="exp-table__num">
                Triggered, sent
              </th>
              <th scope="col" className="exp-table__num">
                Triggered, withheld
              </th>
              <th scope="col" className="exp-table__num">
                Untriggered, sent
              </th>
              <th scope="col" className="exp-table__num">
                Untriggered, withheld
              </th>
              <th scope="col" className="exp-table__num">
                Trigger rate
              </th>
            </tr>
          </thead>
          <tbody>
            {INJECTION_WINDOWS.map((window) => {
              const cells = formatInjectionCells(data, record.key, window);
              return (
                <tr key={window}>
                  <th scope="row">{window}</th>
                  <td className="exp-table__num">{cells.turns}</td>
                  <td className="exp-table__num">{cells.control}</td>
                  <td className="exp-table__num">{cells.triggeredSent}</td>
                  <td className="exp-table__num">{cells.triggeredWithheld}</td>
                  <td className="exp-table__num">{cells.untriggeredSent}</td>
                  <td className="exp-table__num">{cells.untriggeredWithheld}</td>
                  <td className="exp-table__num">{cells.triggerRate}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function GuardrailsTab({
  record,
  data,
}: {
  record: AdminExperimentRecord;
  data: AdminExperimentationResponse;
}) {
  const breaches = data.breaches.filter((b) => b.experiment === record.key);
  return (
    <section className="exp-section">
      <p className="exp-section__facts">
        <Badge tone={data.guardrailsEnabled ? "success" : "warning"}>
          {data.guardrailsEnabled ? "enabled" : "disabled"}
        </Badge>{" "}
        On a breach: <code>{data.guardrailAction}</code>
      </p>
      {breaches.length === 0 ? (
        <p className="exp-section__empty">No breaches on the last tick for this experiment.</p>
      ) : (
        <ul className="exp-breaches">
          {breaches.map((b) => (
            <li className="exp-breaches__row" key={`${b.arm}:${b.guardrail}`}>
              <Badge tone="danger">{b.guardrail}</Badge>
              <span>
                Arm <code>{b.arm}</code>: {b.signal} at {b.armValue} against default{" "}
                {b.defaultValue}. Action: {b.action}.
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function TimelineTab({ experimentKey }: { experimentKey: string }) {
  const lifecycle = useQuery(
    experimentationLifecycleQuery({ entityKey: experimentKey, limit: LIFECYCLE_LIMIT }),
  );
  const events = [...(lifecycle.data?.events ?? [])].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt),
  );
  return (
    <section className="exp-section">
      {lifecycle.isPending ? <LoadingState label="Reading the ledger…" /> : null}
      {lifecycle.isError ? (
        <p className="exp-section__error">{errorMessage(lifecycle.error)}</p>
      ) : null}
      {lifecycle.data && events.length === 0 ? (
        <p className="exp-section__empty">This experiment has not changed state yet.</p>
      ) : null}
      {events.length > 0 ? (
        <div className="exp-table-wrap">
          <table className="exp-table">
            <TableExport name="experiment-events" label="state changes" />
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Entity</th>
                <th scope="col">Transition</th>
                <th scope="col">Actor</th>
                <th scope="col">Reason</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <TimelineRow key={e.id} event={e} />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

function TimelineRow({ event }: { event: AdminLifecycleEvent }) {
  return (
    <tr>
      <td className="exp-table__when">{formatDateTime(event.createdAt)}</td>
      <td>
        <Badge tone="neutral">{event.entityKind}</Badge>
      </td>
      <td>
        <code>{formatTransition(event.fromState, event.toState)}</code>
      </td>
      <td>{event.actorEmail}</td>
      <td>{event.reason}</td>
    </tr>
  );
}

/** What the injection waits for (its declared `when`, from the prompt-section
 *  listing, which is where the declaration is read) beside the whole trigger
 *  vocabulary: where each name comes from and when it is known. */
/** Which trigger sources are DETERMINISTIC: exact facts about the turn, known
 *  without asking a model. An injection that only needs one of these should not
 *  wait on a classifier, so the picker groups them first (plan §7). */
const DETERMINISTIC_SOURCES: readonly string[] = ["stage", "prev", "seller", "funnel"];

function TriggersTab({ record, canAct }: { record: AdminExperimentRecord; canAct: boolean }) {
  const sections = useQuery(promptSectionsQuery());
  const vocabulary = useQuery(experimentationTriggersQuery());
  const injection = sections.data?.prompts
    .flatMap((p) => p.sections)
    .find((s) => s.key === record.key)?.injection;
  return (
    <section className="exp-section">
      {sections.isPending ? <LoadingState label="Reading the declaration…" /> : null}
      {sections.isError ? (
        <p className="exp-section__error">{errorMessage(sections.error)}</p>
      ) : null}
      {injection ? (
        <dl className="exd-facts">
          <dt>Waits for</dt>
          <dd>
            <code>{injection.when}</code>
          </dd>
          <dt>Decided by</dt>
          <dd>
            <code>{injection.source}</code>
          </dd>
          <dt>Timing</dt>
          <dd>
            {injection.awaitedBeforeTheTurn
              ? "Same turn: the verdict is awaited before the agent runs, so the text is in the prompt that answers the very message that triggered it."
              : "Next turn: the verdict lands after the reply is sent, so the earliest this text can reach the model is the following turn."}
          </dd>
        </dl>
      ) : sections.data ? (
        <p className="exp-section__empty">No prompt section declares this injection.</p>
      ) : null}

      <h3 className="exp-lookup__subtitle">Trigger vocabulary</h3>
      <p className="exp-section__sub">
        Every name a turn's fired set may hold, where it comes from, and whether it is known on the
        same turn or the next. Deterministic names are listed first: a stage, a previous turn's
        tool, a seller state or a funnel step is an exact fact about the turn, so an injection that
        only needs one of those should not wait on a classifier. A same-turn name is bound before
        the prompt renders; a next-turn name lands after the reply is sent, so the earliest it can
        steer anything is the following turn.
      </p>
      {vocabulary.isPending ? <LoadingState label="Reading the vocabulary…" /> : null}
      {vocabulary.isError ? (
        <p className="exp-section__error">{errorMessage(vocabulary.error)}</p>
      ) : null}
      {vocabulary.data ? (
        <div className="exp-table-wrap">
          <table className="exp-table">
            <TableExport name="experiment-vocabulary" label="the vocabulary" />
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Source</th>
                <th scope="col">Timing</th>
              </tr>
            </thead>
            <tbody>
              {[...vocabulary.data.triggers]
                .sort((a, b) => {
                  const aDet = DETERMINISTIC_SOURCES.includes(a.source) ? 0 : 1;
                  const bDet = DETERMINISTIC_SOURCES.includes(b.source) ? 0 : 1;
                  return aDet !== bDet ? aDet - bDet : a.name.localeCompare(b.name);
                })
                .map((t) => (
                  <tr
                    key={t.name}
                    className={injection && injection.when.includes(t.name) ? "exd-row--named" : ""}
                  >
                    <td>
                      <code>{t.name}</code>
                    </td>
                    <td>
                      {t.source}
                      {DETERMINISTIC_SOURCES.includes(t.source) ? " (exact)" : " (classified)"}
                    </td>
                    <td>{t.timing === "same_turn" ? "same turn" : "next turn"}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <TrustedTags canAct={canAct} enabled={true} />
    </section>
  );
}

/** What went WRONG, per arm: the error classes on `agent_trace` and the negative
 *  tag findings on `tag_findings`, both over the guardrail window and both keyed
 *  on the same stamp the strip groups by, so the denominators agree.
 *
 *  The strip's error RATE says how often an arm fails. This says how, which is
 *  the difference between "the treatment is worse" and "the treatment times out
 *  against one provider". Served from the A/B snapshot since #1260, so opening
 *  the tab costs a cache read rather than two grouped scans of a day of
 *  telemetry. */
function IssuesTab({ record }: { record: AdminExperimentRecord }) {
  const issues = useQuery(experimentIssuesQuery(record.key, true));
  // The tick's window, not a constant this file keeps: the rollup is measured on
  // the worker now (#1260), so the page reports what was actually counted rather
  // than what it would have asked for.
  const hours = issues.data?.windowHours ?? ISSUES_WINDOW_HOURS;
  return (
    <section className="exp-section">
      <p className="exp-section__sub">
        The last {hours} hours, per arm. Errors come from the traces this experiment stamped;
        negative tags are the findings whose FIRING is the bad outcome (a frustrated client, a match
        failure), which is a property of the tag rather than of this experiment. A rate with no rows
        behind it is a rate nobody can act on, so both lists link into the explorer for the
        conversations themselves.
      </p>

      <h3 className="exp-lookup__subtitle">Error rate per arm</h3>
      {record.strips.length === 0 ? (
        <p className="exp-section__empty">No traces carry this experiment yet.</p>
      ) : (
        <ul className="exp-parity">
          {record.strips.map((strip) => (
            <li className="exp-parity__row" key={strip.arm}>
              <code>{strip.arm}</code>
              <Badge
                tone={
                  strip.errorRate === null || strip.errorRate === undefined
                    ? "neutral"
                    : strip.errorRate > 0
                      ? "warning"
                      : "success"
                }
              >
                {formatRate(strip.errorRate)}
              </Badge>
            </li>
          ))}
        </ul>
      )}

      {issues.isPending ? <LoadingState label="Reading the errors…" /> : null}
      {issues.isError ? <p className="exp-section__error">{errorMessage(issues.error)}</p> : null}
      {issues.data?.partial ? (
        <p className="exp-section__error">
          The A/B tick has not written this rollup yet, so the lists below are incomplete. They are
          not a clean board.
        </p>
      ) : null}

      {issues.data ? (
        <>
          <h3 className="exp-lookup__subtitle">Errors</h3>
          {issues.data.errors.length === 0 ? (
            <p className="exp-section__empty">
              No failed calls carried this experiment in the window.
            </p>
          ) : (
            <div className="exp-table-wrap">
              <table className="exp-table">
                <TableExport name="experiment-failures" label="failed calls" />
                <thead>
                  <tr>
                    <th scope="col">Arm</th>
                    <th scope="col">Error</th>
                    <th scope="col" className="exp-table__num">
                      Calls
                    </th>
                    <th scope="col">Explorer</th>
                  </tr>
                </thead>
                <tbody>
                  {issues.data.errors.map((row) => (
                    <tr key={`${row.arm}:${row.error}`}>
                      <td>
                        <code>{row.arm}</code>
                        {row.arm === issues.data.baseline ? " (baseline)" : ""}
                      </td>
                      <td className="exd-issue__text">{row.error || "(no message)"}</td>
                      <td className="exp-table__num">{row.calls.toLocaleString()}</td>
                      <td>
                        <Link
                          className="ad-link"
                          to="/explorer"
                          search={{ experiment: record.key, arm: row.arm }}
                        >
                          Projects in this arm
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h3 className="exp-lookup__subtitle">Negative tags</h3>
          {issues.data.negativeTags.length === 0 ? (
            <p className="exp-section__empty">
              No negative tag fired on a turn this experiment stamped in the window.
            </p>
          ) : (
            <div className="exp-table-wrap">
              <table className="exp-table">
                <TableExport name="experiment-negative-tags" label="negative tags" />
                <thead>
                  <tr>
                    <th scope="col">Arm</th>
                    <th scope="col">Tag</th>
                    <th scope="col" className="exp-table__num">
                      Findings
                    </th>
                    <th scope="col">Explorer</th>
                  </tr>
                </thead>
                <tbody>
                  {issues.data.negativeTags.map((row) => (
                    <tr key={`${row.arm}:${row.tagger}:${row.tagId}`}>
                      <td>
                        <code>{row.arm}</code>
                        {row.arm === issues.data.baseline ? " (baseline)" : ""}
                      </td>
                      <td>
                        {row.tagName}
                        <span className="exd-issue__hint">
                          {row.tagger}:{row.tagId}
                        </span>
                      </td>
                      <td className="exp-table__num">{row.findings.toLocaleString()}</td>
                      <td>
                        <Link
                          className="ad-link"
                          to="/explorer"
                          search={{ experiment: record.key, arm: row.arm }}
                        >
                          Projects in this arm
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}
